import {
  getPlatform,
  getPlatformTheme,
  getPromotions,
  getRetailproductsAreRetailProductsVisible,
  getShop,
  getShopCategories,
  getShopCustomCategoriesListings,
  getShopDiscounts,
  getSkindropIsVisibleOnMarketplace,
  getUser,
} from "api/methods";
import { STALE_TIME } from "api/staleTimes";
import { fetchCatalogLocationId } from "hooks/useCatalogLocationId";
import { catalogSummaryQuery } from "hooks/useCatalogSummary";
import { prefetchContent } from "modules/routing/contentQueries";
import { membershipsWithUnlockedQuery } from "modules/shopVisibility";
import { queryClient } from "pages/Root";

/**
 * Everything the shop index renders, as individual query options rather than one composite.
 *
 * The route no longer waits for any of it: `prefetchShopIndex` starts the requests, and the page
 * reads the same keys back through `useQuery`. Keeping them separate is what lets the cache work
 * per-endpoint — the catalogue a patient already loaded on another route is reused here instead of
 * being refetched as part of a page-shaped bundle, and one slow endpoint no longer decides when
 * everything else becomes visible.
 */
export const shopContentQueries = {
  platform: () => ({ ...getPlatform.keyed(), staleTime: STALE_TIME.platformConfig }),
  shopConfig: () => ({ ...getShop.keyed() }),
  promotions: () => ({ ...getPromotions.keyed(), staleTime: 1000 * 60 * 2 }),
  memberships: () => membershipsWithUnlockedQuery(),
  categories: () => ({ ...getShopCategories.keyed() }),
  customCategoriesList: () => ({
    ...getShopCustomCategoriesListings.keyed(),
    staleTime: 1000 * 10,
  }),
  shopDiscounts: () => ({ ...getShopDiscounts.keyed(), staleTime: 1000 * 10 }),
  user: () => ({ ...getUser.keyed(), staleTime: 1000 * 60 * 2 }),
  theme: () => ({ ...getPlatformTheme.keyed(), staleTime: STALE_TIME.platformConfig }),
  areRetailProductsVisible: () => ({
    ...getRetailproductsAreRetailProductsVisible.keyed(),
    staleTime: 1000 * 10,
  }),
  // Whether SkinDrop appears is part of the listing filter, so the grid waits for it rather than
  // rendering a catalogue it may have to re-filter. A practice without the endpoint answers `false`
  // instead of failing the page, which is what the old catch-to-false meant.
  isSkinDropVisible: () => ({
    ...getSkindropIsVisibleOnMarketplace.keyed(),
    staleTime: 1000 * 10,
    retry: false,
  }),
} as const;

/**
 * The best-selling rail and every "is there a treatment / package / concern" boolean come from one
 * Algolia summary instead of `GET /listings`; it needs the platform, user and visibility answers
 * first, so it is warmed once those have landed rather than as part of the first wave.
 */
const prefetchCatalogSummary = async () => {
  const [platform, user, areRetailProductsVisible, isSkinDropVisible] = await Promise.all([
    queryClient.fetchQuery(shopContentQueries.platform()),
    queryClient.fetchQuery(shopContentQueries.user()),
    queryClient.fetchQuery(shopContentQueries.areRetailProductsVisible()),
    queryClient.fetchQuery(shopContentQueries.isSkinDropVisible()).catch(() => undefined),
  ]);

  await queryClient.prefetchQuery(
    catalogSummaryQuery({
      platformId: platform.id,
      platformLocationId: await fetchCatalogLocationId(queryClient, user),
      visibility: { areRetailProductsVisible, isSkinDropVisible },
    }),
  );
};

export const prefetchShopIndex = () => {
  const content = prefetchContent(shopContentQueries);
  // A rejection is the consuming `useQuery`'s to report, exactly as for the content queries.
  prefetchCatalogSummary().catch(() => undefined);
  return content;
};
